import React, { useMemo, useState } from 'react';
import { Button, Card, Modal } from '../../design-system';
import { useSelected } from '../../providers/selectedContext';
import { useAppContext } from '../../providers/AppContext';
import styles from './KrAnalysisSummary.module.css';

type KrAnalysisSummaryProps = {
  open: boolean;
  onClose: () => void;
};

type VariableRow = {
  label: string;
  value: string;
};

type CurveRow = {
  id: string;
  point: number;
  values: Record<string, string>;
};

type XYPointRow = {
  id: string;
  point: number;
  x: number;
  y: number;
};

type PlottedCurveRow = {
  id: string;
  chart: string;
  pointCount: number;
  values: XYPointRow[];
  note?: string;
};

const ZOOM_LEVELS = [90, 100, 110, 125] as const;
const DEFAULT_ZOOM = 100;
const EMPTY_VALUE = '—';
const DEFAULT_VISIBLE_PLOTTED_ROWS = 6;

const formatLabel = (key: string) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());

const formatValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : EMPTY_VALUE;
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

const formatPointValue = (value: number) => {
  if (!Number.isFinite(value)) return EMPTY_VALUE;
  return Number(value.toFixed(6)).toString();
};

const buildVariableRows = <T extends object>(
  values: T | null | undefined,
  keyOrder?: (keyof T)[],
): VariableRow[] => {
  if (!values) return [];

  const valueMap = values as Record<string, unknown>;
  const orderedKeys = keyOrder
    ? [...keyOrder, ...Object.keys(valueMap).filter(key => !keyOrder.includes(key as keyof T))]
    : Object.keys(valueMap);

  return orderedKeys.map(key => ({
    label: formatLabel(String(key)),
    value: formatValue(valueMap[String(key)]),
  }));
};

const buildCurveRows = (curves: object): CurveRow[] => {
  const curveMap = curves as Record<string, (number | null)[] | null | undefined>;
  const entries = Object.entries(curveMap).filter(
    ([, values]) => Array.isArray(values) && values.length > 0,
  );

  if (!entries.length) return [];

  const maxLength = Math.max(...entries.map(([, values]) => values?.length ?? 0));

  return Array.from({ length: maxLength }, (_, index) => {
    const values = entries.reduce<Record<string, string>>((accumulator, [key, series]) => {
      accumulator[key] = formatValue(series?.[index]);
      return accumulator;
    }, {});

    return {
      id: `curve-row-${index}`,
      point: index + 1,
      values,
    };
  });
};

const buildXYRows = (
  xValues: (number | null)[] | null | undefined,
  yValues: (number | null)[] | null | undefined,
): XYPointRow[] => {
  const maxLength = Math.max(xValues?.length ?? 0, yValues?.length ?? 0);
  const rows: XYPointRow[] = [];

  for (let index = 0; index < maxLength; index += 1) {
    const x = xValues?.[index];
    const y = yValues?.[index];

    if (typeof x === 'number' && Number.isFinite(x) && typeof y === 'number' && Number.isFinite(y)) {
      rows.push({
        id: `xy-${index}`,
        point: rows.length + 1,
        x,
        y,
      });
    }
  }

  return rows;
};

const SummaryTable = ({
  columns,
  rows,
  emptyMessage,
  zoom,
  alignRightFrom = 1,
}: {
  columns: string[];
  rows: React.ReactNode[][];
  emptyMessage: string;
  zoom: number;
  alignRightFrom?: number;
}) => (
  <div className={styles.tableWrap}>
    <table
      className={styles.summaryTable}
      style={{ '--summary-zoom': `${zoom / 100}` } as React.CSSProperties}
    >
      <thead>
        <tr>
          {columns.map((column, index) => (
            <th key={column} className={index >= alignRightFrom ? styles.numericCell : undefined}>
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length ? (
          rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className={cellIndex >= alignRightFrom ? styles.numericCell : undefined}>
                  {cell}
                </td>
              ))}
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={columns.length} className={styles.emptyCell}>
              {emptyMessage}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const VariableTableCard = ({
  title,
  subtitle,
  rows,
  zoom,
}: {
  title: string;
  subtitle?: string;
  rows: VariableRow[];
  zoom: number;
}) => (
  <Card variant="outlined" fullWidth>
    <Card.Header title={title} subtitle={subtitle} />
    <Card.Body>
      <SummaryTable
        columns={['Variable', 'Value']}
        rows={rows.map(row => [row.label, row.value])}
        emptyMessage="No values are currently available for this section."
        zoom={zoom}
      />
    </Card.Body>
  </Card>
);

const CurveTableCard = ({
  title,
  subtitle,
  rows,
  zoom,
}: {
  title: string;
  subtitle?: string;
  rows: CurveRow[];
  zoom: number;
}) => {
  const valueKeys = Object.keys(rows[0]?.values ?? {});

  return (
    <Card variant="outlined" fullWidth>
      <Card.Header title={title} subtitle={subtitle} />
      <Card.Body>
        <SummaryTable
          columns={['Point', ...valueKeys.map(formatLabel)]}
          rows={rows.map(row => [row.point, ...valueKeys.map(key => row.values[key])])}
          emptyMessage="No curve values are currently available for this section."
          zoom={zoom}
        />
      </Card.Body>
    </Card>
  );
};

const ValuesTable = ({ rows, zoom }: { rows: XYPointRow[]; zoom: number }) => (
  <div className={styles.valuesTableWrap}>
    <table
      className={styles.valuesTable}
      style={{ '--summary-zoom': `${zoom / 100}` } as React.CSSProperties}
    >
      <thead>
        <tr>
          <th>#</th>
          <th>X</th>
          <th>Y</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr key={row.id}>
            <td>{row.point}</td>
            <td>{formatPointValue(row.x)}</td>
            <td>{formatPointValue(row.y)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PlottedCurvesTable = ({ rows, zoom }: { rows: PlottedCurveRow[]; zoom: number }) => {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  return (
    <div className={styles.tableWrap}>
      <table
        className={`${styles.summaryTable} ${styles.plottedSummaryTable}`}
        style={{ '--summary-zoom': `${zoom / 100}` } as React.CSSProperties}
      >
        <colgroup>
          <col className={styles.plottedChartCol} />
          <col className={styles.plottedCountCol} />
          <col className={styles.plottedValuesCol} />
        </colgroup>
        <thead>
          <tr>
            <th>Series</th>
            <th className={styles.numericCell}>Point Count</th>
            <th>Values (X = Saturation, Y = Kr)</th>
          </tr>
        </thead>
        <tbody>
          {rows.length ? (
            rows.map(row => {
              const isExpanded = expandedRows[row.id] ?? false;
              const visibleRows = isExpanded ? row.values : row.values.slice(0, DEFAULT_VISIBLE_PLOTTED_ROWS);
              const hasMore = row.values.length > DEFAULT_VISIBLE_PLOTTED_ROWS;

              return (
                <tr key={row.id}>
                  <td>{row.chart}</td>
                  <td className={styles.numericCell}>{row.pointCount}</td>
                  <td>
                    <div className={styles.valuesCell}>
                      {row.note ? <p className={styles.resampleNote}>{row.note}</p> : null}
                      <ValuesTable rows={visibleRows} zoom={zoom} />
                      {hasMore ? (
                        <div className={styles.valuesActions}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setExpandedRows(current => ({
                                ...current,
                                [row.id]: !isExpanded,
                              }))
                            }
                          >
                            {isExpanded
                              ? 'Compress'
                              : `Expand to see more (${row.values.length - DEFAULT_VISIBLE_PLOTTED_ROWS} more)`}
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={3} className={styles.emptyCell}>
                No Kr curves are currently available.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const KrAnalysisSummary = ({ open, onClose }: KrAnalysisSummaryProps) => {
  const [zoom, setZoom] = useState<number>(DEFAULT_ZOOM);
  const { selectedInfo } = useSelected();
  const {
    data: { wells },
  } = useAppContext();

  const sample = selectedInfo.sample ?? null;
  const well = wells.find(entry => entry.uid === sample?.well_uid);

  const inputVariableRows = useMemo(() => {
    if (!sample) return [];

    const values: Record<string, unknown> = {
      sample_code: sample.sample_code,
      well: well?.well_name ?? selectedInfo.well?.well_name,
      depth: sample.depth,
      depth_unit: sample.depth_unit,
      core_sample_length: sample.sample_length,
      porosity: sample.input_sample_values?.porosity,
      permeability: sample.input_sample_values?.permeability,
      oil_permeability_ko: sample.input_sample_values?.ko,
      krw: sample.input_sample_values?.krw,
      krg: sample.input_sample_values?.krg,
      swi: sample.input_sample_values?.swi,
      project: selectedInfo.project?.project_name,
    };

    return buildVariableRows(values);
  }, [sample, selectedInfo.project?.project_name, selectedInfo.well?.well_name, well?.well_name]);

  // Water-Oil
  const woInputCurveRows = useMemo(
    () =>
      buildCurveRows({
        sw: sample?.input_sample_curves.water_oil?.sw_krel,
        kro_lab: sample?.input_sample_curves.water_oil?.kro_lab,
        krw_lab: sample?.input_sample_curves.water_oil?.krw_lab,
      }),
    [sample],
  );

  const woValuesRows = useMemo(() => {
    const wo = sample?.k_rel_values?.water_oil;
    if (!wo) return [];
    return buildVariableRows(wo, ['no_w', 'nw', 'true_no_w', 'true_nw', 'sor_w', 'true_sor_w', 'krw_exponent', 'true_krw', 'true_kro_w']);
  }, [sample]);

  const woKrCurveRows = useMemo<PlottedCurveRow[]>(() => {
    const curves = sample?.k_rel_curves?.water_oil;
    if (!curves) return [];

    const rows: PlottedCurveRow[] = [];

    const kroPoints = buildXYRows(curves.swn, curves.kro_corrected);
    if (kroPoints.length) {
      rows.push({ id: 'wo-kro-corrected', chart: 'Kro Corrected', pointCount: kroPoints.length, values: kroPoints });
    }

    const krwPoints = buildXYRows(curves.swn, curves.krw_corrected);
    if (krwPoints.length) {
      rows.push({ id: 'wo-krw-corrected', chart: 'Krw Corrected', pointCount: krwPoints.length, values: krwPoints });
    }

    return rows;
  }, [sample]);

  const woExponentRows = useMemo(
    () => {
      const curves = sample?.k_rel_curves?.water_oil;
      if (!curves) return [];
      return buildCurveRows({
        son: curves.son,
        swn: curves.swn,
        no_exponent: curves.no_exponent,
        nw_exponent: curves.nw_exponent,
      });
    },
    [sample],
  );

  // Gas-Oil
  const goInputCurveRows = useMemo(
    () =>
      buildCurveRows({
        sg: sample?.input_sample_curves.gas_oil?.sg_krel,
        kro_lab: sample?.input_sample_curves.gas_oil?.kro_lab,
        krg_lab: sample?.input_sample_curves.gas_oil?.krg_lab,
      }),
    [sample],
  );

  const goValuesRows = useMemo(() => {
    const go = sample?.k_rel_values?.gas_oil;
    if (!go) return [];
    return buildVariableRows(go, ['no_g', 'ng', 'true_no_g', 'true_ng', 'sor_g', 'true_sor_g', 'sgc', 'krg_exponent', 'true_krg', 'true_kro_g']);
  }, [sample]);

  const goKrCurveRows = useMemo<PlottedCurveRow[]>(() => {
    const curves = sample?.k_rel_curves?.gas_oil;
    if (!curves) return [];

    const rows: PlottedCurveRow[] = [];

    const kroPoints = buildXYRows(curves.sgn, curves.kro_corrected);
    if (kroPoints.length) {
      rows.push({ id: 'go-kro-corrected', chart: 'Kro Corrected', pointCount: kroPoints.length, values: kroPoints });
    }

    const krgPoints = buildXYRows(curves.sgn, curves.krg_corrected);
    if (krgPoints.length) {
      rows.push({ id: 'go-krg-corrected', chart: 'Krg Corrected', pointCount: krgPoints.length, values: krgPoints });
    }

    return rows;
  }, [sample]);

  const goExponentRows = useMemo(
    () => {
      const curves = sample?.k_rel_curves?.gas_oil;
      if (!curves) return [];
      return buildCurveRows({
        son: curves.son,
        sgn: curves.sgn,
        no_exponent: curves.no_exponent,
        ng_exponent: curves.ng_exponent,
      });
    },
    [sample],
  );

  const hasWaterOil =
    woInputCurveRows.length > 0 || woValuesRows.length > 0 || woKrCurveRows.length > 0;
  const hasGasOil =
    goInputCurveRows.length > 0 || goValuesRows.length > 0 || goKrCurveRows.length > 0;

  const zoomLabel = `${zoom}%`;

  if (!sample) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Sample Summary · ${sample.sample_code}`}
      size="lg"
      className={styles.summaryModal}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className={styles.summaryRoot}>
        <div className={styles.summaryTopBar}>
          <p className={styles.summaryIntro}>
            This view consolidates the current sample state used across Kr Analysis and shows the stored
            Kr curve data, Corey exponents, and lab measurements for both Water-Oil and Gas-Oil analyses.
          </p>
          <div className={styles.zoomControls}>
            <span className={styles.zoomLabel}>Zoom {zoomLabel}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setZoom(current => ZOOM_LEVELS[Math.max(0, ZOOM_LEVELS.indexOf(current as 90 | 100 | 110 | 125) - 1)] ?? current)}
              disabled={zoom === ZOOM_LEVELS[0]}
            >
              Zoom Out
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setZoom(DEFAULT_ZOOM)}
              disabled={zoom === DEFAULT_ZOOM}
            >
              Reset
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setZoom(current => ZOOM_LEVELS[Math.min(ZOOM_LEVELS.length - 1, ZOOM_LEVELS.indexOf(current as 90 | 100 | 110 | 125) + 1)] ?? current)}
              disabled={zoom === ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
            >
              Zoom In
            </Button>
          </div>
        </div>

        <div className={styles.sectionStack}>
          <VariableTableCard
            title="Input Variables"
            subtitle="Current values from sample, well, and project context."
            rows={inputVariableRows}
            zoom={zoom}
          />

          {hasWaterOil && (
            <>
              {woValuesRows.length > 0 && (
                <VariableTableCard
                  title="Water-Oil · Corey Exponents"
                  subtitle="Calculated Corey exponent values currently stored on the sample."
                  rows={woValuesRows}
                  zoom={zoom}
                />
              )}

              {woInputCurveRows.length > 0 && (
                <CurveTableCard
                  title="Water-Oil · Input Lab Curves"
                  subtitle="Laboratory-measured Sw, Kro, and Krw values provided as input."
                  rows={woInputCurveRows}
                  zoom={zoom}
                />
              )}

              {woKrCurveRows.length > 0 && (
                <Card variant="outlined" fullWidth>
                  <Card.Header
                    title="Water-Oil · Kr Curves"
                    subtitle="Corrected Kr curves stored on the sample (X = Swn, Y = Kr)."
                  />
                  <Card.Body>
                    <PlottedCurvesTable rows={woKrCurveRows} zoom={zoom} />
                  </Card.Body>
                </Card>
              )}

              {woExponentRows.length > 0 && (
                <CurveTableCard
                  title="Water-Oil · Exponents per Point"
                  subtitle="No and Nw Corey exponent values at each saturation point."
                  rows={woExponentRows}
                  zoom={zoom}
                />
              )}
            </>
          )}

          {hasGasOil && (
            <>
              {goValuesRows.length > 0 && (
                <VariableTableCard
                  title="Gas-Oil · Corey Exponents"
                  subtitle="Calculated Corey exponent values currently stored on the sample."
                  rows={goValuesRows}
                  zoom={zoom}
                />
              )}

              {goInputCurveRows.length > 0 && (
                <CurveTableCard
                  title="Gas-Oil · Input Lab Curves"
                  subtitle="Laboratory-measured Sg, Kro, and Krg values provided as input."
                  rows={goInputCurveRows}
                  zoom={zoom}
                />
              )}

              {goKrCurveRows.length > 0 && (
                <Card variant="outlined" fullWidth>
                  <Card.Header
                    title="Gas-Oil · Kr Curves"
                    subtitle="Corrected Kr curves stored on the sample (X = Sgn, Y = Kr)."
                  />
                  <Card.Body>
                    <PlottedCurvesTable rows={goKrCurveRows} zoom={zoom} />
                  </Card.Body>
                </Card>
              )}

              {goExponentRows.length > 0 && (
                <CurveTableCard
                  title="Gas-Oil · Exponents per Point"
                  subtitle="No and Ng Corey exponent values at each saturation point."
                  rows={goExponentRows}
                  zoom={zoom}
                />
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default KrAnalysisSummary;
